import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  MapPin,
  School,
  Users,
  ChevronUp,
  FolderOpen,
  Mail,
  Phone,
  RotateCw,
} from "lucide-react";
import {
  getTeacherAccountsBySchools,
  getTeacherAccounts,
  getTeacherAccountTeachingLevelStats,
  deleteTeacherAccount,
  resetTeacherAccountPassword,
} from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";
import { grantDocumentAccessBonus } from "../../../api/elearning/documents/documentAccess";
import {
  showConfirmDialog,
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import Pagination from "../../../components/common/Pagination";
import TeacherAccountFilter from "./TeacherAccountFilter";
import TeacherAccountFormModal from "./TeacherAccountFormModal";
import TeacherAccountImportModal from "./TeacherAccountImportModal";
import TeacherAccountMiniDashboard from "./TeacherAccountMiniDashboard";
import TeachersBySchoolModal from "./TeachersBySchoolModal";
import TeacherAccountRowActionMenu from "./TeacherAccountRowActionMenu";
import TeacherDeviceManageModal from "./TeacherDeviceManageModal";
import TeacherAccountDetailModal from "./TeacherAccountDetailModal";

const VIEW_MODE_STORAGE_KEY = "teacher_account_view_mode";

const TeacherAccountCardPage = () => {
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEW_MODE_STORAGE_KEY) || "school";
    } catch {
      return "school";
    }
  });

  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    school_count: 0,
    mam_non: 0,
    tieu_hoc: 0,
    thcs: 0,
    thpt: 0,
    chua_phan_loai: 0,
  });

  const [filters, setFilters] = useState({
    keyword: "",
    branch_id: "",
    customer_id: "",
    status: "",
    school_level: "",
  });

  const [debouncedKeyword] = useDebounce(filters.keyword, 400);
  const [branches, setBranches] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [teacherListVersion, setTeacherListVersion] = useState(0);

  // Quản lý đóng/mở từng nhóm trường khi xem theo tài khoản
  const [collapsedSchools, setCollapsedSchools] = useState(new Set());

  // Action states cho chế độ xem theo tài khoản
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [deviceTeacher, setDeviceTeacher] = useState(null);
  const [detailTeacherId, setDetailTeacherId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState("account");

  const changeViewMode = (mode) => {
    setViewMode(mode);
    setCurrentPage(1);
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {}
  };

  const loadBranches = useCallback(async () => {
    try {
      const b = await getAllBranches_Array();
      setBranches(Array.isArray(b) ? b : []);
    } catch {
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    if (!filters.branch_id) {
      setCustomerOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await getCustomersByBranch(filters.branch_id);
        const list = res?.items || res || [];
        setCustomerOptions(Array.isArray(list) ? list : []);
      } catch {
        setCustomerOptions([]);
      }
    })();
  }, [filters.branch_id]);

  const handleFiltersChange = useCallback((next) => {
    setFilters((prev) => {
      if (
        next.branch_id !== prev.branch_id ||
        next.customer_id !== prev.customer_id ||
        next.status !== prev.status
      ) {
        setCurrentPage(1);
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    setCurrentPage(1);
  }, [debouncedKeyword, filters.school_level]);

  const baseFilterParams = useCallback(
    () => ({
      keyword: debouncedKeyword || undefined,
      branch_id: filters.branch_id || undefined,
      customer_id: filters.customer_id || undefined,
      status: filters.status || undefined,
      school_level: filters.school_level || undefined,
    }),
    [
      debouncedKeyword,
      filters.branch_id,
      filters.customer_id,
      filters.status,
      filters.school_level,
    ]
  );

  const fetchStats = useCallback(async () => {
    try {
      const statsRes = await getTeacherAccountTeachingLevelStats({
        keyword: debouncedKeyword || undefined,
        branch_id: filters.branch_id || undefined,
        customer_id: filters.customer_id || undefined,
        status: filters.status || undefined,
      });
      setStats({
        total: Number(statsRes?.total || 0),
        school_count: Number(statsRes?.school_count || 0),
        mam_non: Number(statsRes?.mam_non || 0),
        tieu_hoc: Number(statsRes?.tieu_hoc || 0),
        thcs: Number(statsRes?.thcs || 0),
        thpt: Number(statsRes?.thpt || 0),
        chua_phan_loai: Number(statsRes?.chua_phan_loai || 0),
      });
    } catch {
      setStats({
        total: 0,
        school_count: 0,
        mam_non: 0,
        tieu_hoc: 0,
        thcs: 0,
        thpt: 0,
        chua_phan_loai: 0,
      });
    }
  }, [
    debouncedKeyword,
    filters.branch_id,
    filters.customer_id,
    filters.status,
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === "school") {
        const res = await getTeacherAccountsBySchools({
          ...baseFilterParams(),
          page: currentPage,
          pageSize,
        });
        setSchools(Array.isArray(res?.items) ? res.items : []);
        setTotalRecords(Number(res?.total ?? 0));
      } else {
        const res = await getTeacherAccounts({
          ...baseFilterParams(),
          sort_by: "school",
          page: currentPage,
          pageSize,
        });
        setTeachers(Array.isArray(res?.items) ? res.items : []);
        setTotalRecords(Number(res?.total ?? 0));
      }
    } catch (e) {
      console.error(e);
      showErrorToast(
        viewMode === "school"
          ? "Không tải được danh sách trường."
          : "Không tải được danh sách tài khoản giáo viên."
      );
      if (viewMode === "school") setSchools([]);
      else setTeachers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [baseFilterParams, currentPage, pageSize, viewMode, teacherListVersion]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchStats(), fetchData()]);
  }, [fetchStats, fetchData]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectLevel = (schoolLevel) => {
    setFilters((prev) => ({ ...prev, school_level: schoolLevel }));
    setSelectedSchool(null);
    setCurrentPage(1);
  };

  // Gom nhóm tài khoản theo trường khi ở chế độ "Theo tài khoản"
  const groupedTeachersBySchool = useMemo(() => {
    if (viewMode !== "account" || !teachers.length) return [];
    const map = new Map();

    for (const emp of teachers) {
      const schoolKey = emp.partner_customer_id || "unassigned";
      if (!map.has(schoolKey)) {
        map.set(schoolKey, {
          schoolId: schoolKey,
          schoolName: emp.partner_school_name || "Chưa gắn trường học",
          schoolLevel: emp.school_level,
          branchName: emp.branch_name,
          teachers: [],
        });
      }
      map.get(schoolKey).teachers.push(emp);
    }

    return Array.from(map.values());
  }, [teachers, viewMode]);

  const toggleSchoolCollapse = (schoolId) => {
    setCollapsedSchools((prev) => {
      const next = new Set(prev);
      if (next.has(schoolId)) next.delete(schoolId);
      else next.add(schoolId);
      return next;
    });
  };

  const collapseAllSchools = () => {
    setCollapsedSchools(new Set(groupedTeachersBySchool.map((g) => g.schoolId)));
  };

  const expandAllSchools = () => {
    setCollapsedSchools(new Set());
  };

  const schoolLevelBadge = (level) => {
    if (!level) {
      return (
        <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
          Chưa phân loại
        </span>
      );
    }
    return (
      <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-700">
        {level}
      </span>
    );
  };

  // Các handler thao tác trên dòng tài khoản GV
  const handleResetPassword = async (teacher) => {
    const confirm = await showConfirmDialog({
      title: "Reset mật khẩu?",
      text: `Hệ thống sẽ tạo mật khẩu tạm mới và gửi email tới ${teacher.email || teacher.name}.`,
      confirmText: "Reset mật khẩu",
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = await resetTeacherAccountPassword(teacher.id);
      showSuccessToast(res?.message || "Đã reset mật khẩu và gửi email.");
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không reset được mật khẩu.");
    }
  };

  const handleGrantQuota = async (teacher) => {
    const confirm = await showConfirmDialog({
      title: "Thêm lượt tải?",
      text: `Cấp thêm 5 lượt tải/xem tài liệu cho ${teacher.name}.`,
      confirmText: "Cấp +5 lượt",
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = await grantDocumentAccessBonus(teacher.id, { packs: 1 });
      showSuccessToast(res?.message || "Đã cấp thêm 5 lượt.");
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Không cấp được lượt tải."
      );
    }
  };

  const handleDeleteTeacher = async (teacher) => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.teacher_account,
      name: teacher?.name,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteTeacherAccount(teacher.id);
      showSuccessToast("Đã xóa tài khoản.");
      setTeacherListVersion((v) => v + 1);
      refreshAll();
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không xóa được.");
    }
  };

  const openTeacherDetail = (teacher, tab = "account") => {
    setDetailInitialTab(tab);
    setDetailTeacherId(teacher.id);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50/30 p-3 pb-24 md:p-8 md:pb-8">
      {/* 1. Header (Chuẩn §1.2 Design.md) */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="teacher-accounts-header">
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Tài khoản <span className="text-red-700">giáo viên đối tác</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                {viewMode === "school"
                  ? "Chế độ xem: Theo trường học (đơn vị đối tác CRM)"
                  : "Chế độ xem: Theo tài khoản (nhóm theo trường học)"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Mini Dashboard (Chuẩn §1.7 Design.md) */}
      <TeacherAccountMiniDashboard
        stats={stats}
        selectedLevel={filters.school_level}
        onSelectLevel={handleSelectLevel}
        loading={loading}
      />

      {/* 3. Filter Bar kèm Segmented View Mode Toggle */}
      <TeacherAccountFilter
        filters={filters}
        onChange={handleFiltersChange}
        branchOptions={branches}
        customerOptions={customerOptions}
        viewMode={viewMode}
        onViewModeChange={changeViewMode}
        onNewClick={() => {
          setEditId(null);
          setShowForm(true);
        }}
        onImportClick={() => setShowImport(true)}
        onRefresh={refreshAll}
      />

      {/* Toolbar phụ khi xem theo tài khoản: Thu gọn / Mở rộng tất cả */}
      {viewMode === "account" && groupedTeachersBySchool.length > 0 && (
        <div className="mb-3 flex items-center justify-between text-xs text-gray-500 px-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-700">
              {groupedTeachersBySchool.length} trường
            </span>
            <span>•</span>
            <span>{totalRecords} tài khoản giáo viên</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={expandAllSchools}
              className="font-bold text-gray-600 hover:text-red-700 transition"
            >
              Mở rộng tất cả
            </button>
            <span>|</span>
            <button
              type="button"
              onClick={collapseAllSchools}
              className="font-bold text-gray-600 hover:text-red-700 transition"
            >
              Thu gọn tất cả
            </button>
          </div>
        </div>
      )}

      {/* 4. Table view */}
      <div className="mt-2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-auto">
          {viewMode === "school" ? (
            /* ==================== CHẾ ĐỘ 1: THEO TRƯỜNG HỌC ==================== */
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-gray-50/90">
                <tr className="text-left text-gray-500">
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                    Trường / đơn vị
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                    Cấp học
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                    Chi nhánh
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide">
                    Giáo viên
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide">
                    Đang hoạt động
                  </th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody>
                {schools.map((school) => (
                  <tr
                    key={school.partner_customer_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedSchool(school)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && setSelectedSchool(school)
                    }
                    className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-red-50/40"
                  >
                    <td className="px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-700">
                          <School className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-bold text-gray-900">
                            {school.partner_school_name}
                          </p>
                          {school.ward ? (
                            <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500">
                              <MapPin className="h-3 w-3 shrink-0" />
                              {school.ward}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{schoolLevelBadge(school.school_level)}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {school.branch_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-800">
                        <Users className="h-4 w-4 text-gray-400" />
                        {Number(school.teacher_count || 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-emerald-700">
                        {Number(school.active_count || 0)}
                      </span>
                      {Number(school.inactive_count || 0) > 0 ? (
                        <span className="ml-1 text-xs text-gray-400">
                          / {Number(school.inactive_count)} ngưng
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300">
                      <ChevronRight className="ml-auto h-5 w-5" />
                    </td>
                  </tr>
                ))}
                {!loading && schools.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      Không có trường nào phù hợp bộ lọc.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : (
            /* ==================== CHẾ ĐỘ 2: THEO TÀI KHOẢN (NHÓM THEO TRƯỜNG) ==================== */
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-gray-50/90 border-b border-gray-100">
                <tr className="text-left text-gray-500">
                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide">
                    Giáo viên / Nhân sự
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                    Email đăng nhập
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                    Điện thoại
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                    Vị trí / Chức danh
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide">
                    Trạng thái
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide w-28">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody>
                {groupedTeachersBySchool.map((group) => {
                  const isCollapsed = collapsedSchools.has(group.schoolId);

                  return (
                    <React.Fragment key={group.schoolId}>
                      {/* School Group Header Row */}
                      <tr
                        onClick={() => toggleSchoolCollapse(group.schoolId)}
                        className="cursor-pointer border-t-2 border-gray-200/80 bg-slate-100/80 hover:bg-slate-200/70 transition-colors select-none"
                      >
                        <td colSpan={6} className="px-4 py-2.5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <button
                                type="button"
                                className="p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-white/80 transition"
                                title={isCollapsed ? "Mở rộng danh sách" : "Thu gọn danh sách"}
                              >
                                {isCollapsed ? (
                                  <ChevronRight size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )}
                              </button>
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-700 shadow-xs">
                                <School size={16} />
                              </div>
                              <span className="font-black text-gray-900 truncate text-sm">
                                {group.schoolName}
                              </span>
                              {group.schoolLevel && (
                                <span className="hidden sm:inline-flex">
                                  {schoolLevelBadge(group.schoolLevel)}
                                </span>
                              )}
                              {group.branchName && (
                                <span className="hidden md:inline text-xs text-gray-500">
                                  ({group.branchName})
                                </span>
                              )}
                            </div>

                            <div className="shrink-0 flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-bold text-gray-700 shadow-xs">
                                <Users size={13} className="text-gray-400" />
                                {group.teachers.length} giáo viên
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Sub-rows: Teachers in this school */}
                      {!isCollapsed &&
                        group.teachers.map((emp) => (
                          <tr
                            key={emp.id}
                            onClick={() => openTeacherDetail(emp)}
                            className={`cursor-pointer border-t border-gray-100 bg-white transition-colors hover:bg-red-50/30 ${
                              openActionMenuId === emp.id ? "relative z-40" : ""
                            }`}
                          >
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <img
                                  src={
                                    emp.avatar_url ||
                                    "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                                  }
                                  className="h-9 w-9 shrink-0 rounded-full border border-gray-200 object-cover"
                                  alt=""
                                />
                                <div className="min-w-0">
                                  <p className="font-bold text-gray-900 hover:text-red-700 truncate">
                                    {emp.name}
                                  </p>
                                  {emp.code && (
                                    <p className="text-xs text-gray-400 font-mono">
                                      {emp.code}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              <span className="flex items-center gap-1.5 text-xs">
                                <Mail size={13} className="text-gray-400 shrink-0" />
                                <span className="truncate">{emp.email || "—"}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              <span className="flex items-center gap-1.5 text-xs">
                                <Phone size={13} className="text-gray-400 shrink-0" />
                                <span>{emp.phone || "—"}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600 font-medium">
                              {emp.position_display || "Giáo viên đối tác"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide border ${
                                  emp.is_active
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-red-200 bg-red-50 text-red-700"
                                }`}
                              >
                                {emp.is_active ? "Đang hoạt động" : "Ngưng sử dụng"}
                              </span>
                            </td>
                            <td
                              className="px-4 py-3 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TeacherAccountRowActionMenu
                                teacher={emp}
                                isOpen={openActionMenuId === emp.id}
                                onOpenChange={(open) =>
                                  setOpenActionMenuId(open ? emp.id : null)
                                }
                                onResetPassword={handleResetPassword}
                                onChangeDevice={setDeviceTeacher}
                                onGrantQuota={handleGrantQuota}
                                onViewDetail={(t) => openTeacherDetail(t)}
                                onEdit={(t) => {
                                  setEditId(t.id);
                                  setShowForm(true);
                                }}
                                onDelete={handleDeleteTeacher}
                              />
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}

                {!loading && groupedTeachersBySchool.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      Không có tài khoản giáo viên nào phù hợp bộ lọc.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 5. Phân trang */}
      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      {/* 6. Các Modals */}
      {/* Modal danh sách GV theo trường khi ở chế độ xem trường */}
      <TeachersBySchoolModal
        isOpen={!!selectedSchool}
        school={selectedSchool}
        statusFilter={filters.status}
        schoolLevelFilter={filters.school_level}
        refreshVersion={teacherListVersion}
        onClose={() => setSelectedSchool(null)}
        onEdit={(id) => {
          setEditId(id);
          setShowForm(true);
        }}
        onChanged={refreshAll}
      />

      {/* Modal tạo mới / chỉnh sửa tài khoản */}
      <TeacherAccountFormModal
        isOpen={showForm}
        employeeId={editId}
        onClose={() => {
          setShowForm(false);
          setEditId(null);
        }}
        onSaved={() => {
          setTeacherListVersion((v) => v + 1);
          refreshAll();
        }}
      />

      {/* Modal nhập Excel */}
      <TeacherAccountImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onRefresh={refreshAll}
      />

      {/* Modal quản lý thiết bị (dành cho chế độ xem theo tài khoản) */}
      <TeacherDeviceManageModal
        isOpen={!!deviceTeacher}
        teacher={deviceTeacher}
        onClose={() => setDeviceTeacher(null)}
        onChanged={() => {
          setTeacherListVersion((v) => v + 1);
          refreshAll();
        }}
      />

      {/* Modal xem chi tiết tài khoản GV (dành cho chế độ xem theo tài khoản) */}
      <TeacherAccountDetailModal
        isOpen={!!detailTeacherId}
        teacherId={detailTeacherId}
        initialTab={detailInitialTab}
        onClose={() => setDetailTeacherId(null)}
        onEdit={(id) => {
          setEditId(id);
          setShowForm(true);
        }}
        onChanged={() => {
          setTeacherListVersion((v) => v + 1);
          refreshAll();
        }}
      />
    </div>
  );
};

export default TeacherAccountCardPage;
